import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { createCheckoutSession, getPaymentStatus, getListing } from '../services/api';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import Footer from '../components/layout/Footer';
import { 
  ArrowLeft, Check, Loader2, CheckCircle, 
  Shield, Clock, Crown, Building2, MapPin, Star,
  MessageSquare, ChevronDown, ChevronUp, Sparkles, 
  Heart, Users, Zap, Lock, ArrowRight, BadgeCheck
} from 'lucide-react';

const UpgradeForMatchPage = () => {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  
  const [listing, setListing] = useState(null);
  const [loadingListing, setLoadingListing] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [viewerCount] = useState(Math.floor(Math.random() * 8) + 12);

  const plans = [
    { id: 'weekly', duration: '1 week', price: '4,99', weekPrice: '4,99', popular: false, color: 'from-gray-500 to-gray-600' },
    { id: 'monthly', duration: '4 weken', price: '14,99', weekPrice: '3,75', popular: true, color: 'from-[#2E7D32] to-[#1B5E20]', save: '25%' },
    { id: 'quarterly', duration: '12 weken', price: '29,99', weekPrice: '2,50', popular: false, color: 'from-[#1F3A5F] to-[#0D2137]', save: '50%' }
  ];

  const features = [
    { icon: MessageSquare, text: language === 'nl' ? 'Onbeperkt berichten' : 'Unlimited messages', highlight: true },
    { icon: Zap, text: language === 'nl' ? 'Direct contact' : 'Direct contact', highlight: false },
    { icon: BadgeCheck, text: language === 'nl' ? 'Geverifieerde verhuurders' : 'Verified landlords', highlight: false },
    { icon: Shield, text: language === 'nl' ? 'Veilig & betrouwbaar' : 'Safe & secure', highlight: false },
    { icon: Clock, text: language === 'nl' ? 'Op elk moment opzegbaar' : 'Cancel anytime', highlight: false }
  ];

  const testimonials = [
    { 
      name: 'Sophie de Vries', 
      city: 'Amsterdam', 
      text: language === 'nl' ? 'Binnen 5 dagen een studio gevonden in De Pijp! Na maanden zoeken op andere sites was dit echt een verademing.' : 'Found a studio in De Pijp within 5 days! After months on other sites, this was a relief.', 
      rating: 5, 
      initial: 'S',
      color: 'bg-pink-500'
    },
    { 
      name: 'Daan Bakker', 
      city: 'Rotterdam', 
      text: language === 'nl' ? 'Eindelijk een platform waar verhuurders serieus reageren. Binnen een week 3 bezichtigingen gehad!' : 'Finally a platform where landlords respond seriously. Had 3 viewings within a week!', 
      rating: 5, 
      initial: 'D',
      color: 'bg-blue-500'
    },
    { 
      name: 'Lisa Jansen', 
      city: 'Utrecht', 
      text: language === 'nl' ? 'Als internationale student leek het onmogelijk. Via Swipe je woning binnen 2 weken mijn droomkamer gevonden.' : 'As an international student it seemed impossible. Found my dream room via Swipe je woning in 2 weeks.', 
      rating: 5, 
      initial: 'L',
      color: 'bg-purple-500'
    },
    { 
      name: 'Mark van den Berg', 
      city: 'Den Haag', 
      text: language === 'nl' ? 'Super handige app! Swipen werkt veel fijner dan eindeloos scrollen. Aanrader voor iedereen die zoekt.' : 'Super handy app! Swiping works much better than endless scrolling. Recommended!', 
      rating: 5, 
      initial: 'M',
      color: 'bg-green-500'
    },
    { 
      name: 'Emma Visser', 
      city: 'Eindhoven', 
      text: language === 'nl' ? 'Verhuisd voor werk en had snel iets nodig. Binnen 10 dagen mijn appartement gevonden. Top service!' : 'Moved for work and needed something fast. Found my apartment within 10 days. Great service!', 
      rating: 5, 
      initial: 'E',
      color: 'bg-orange-500'
    },
    { 
      name: 'Thomas Smit', 
      city: 'Groningen', 
      text: language === 'nl' ? 'Het premium abonnement is elke euro waard. Direct contact met verhuurders maakt het verschil.' : 'The premium subscription is worth every euro. Direct contact with landlords makes the difference.', 
      rating: 5, 
      initial: 'T',
      color: 'bg-teal-500'
    }
  ];

  const faqs = [
    { q: language === 'nl' ? 'Kan ik op elk moment opzeggen?' : 'Can I cancel anytime?', a: language === 'nl' ? 'Ja! Je kunt direct opzeggen via je account. Je behoudt toegang tot het einde van je periode.' : 'Yes! Cancel directly via your account. Keep access until period ends.' },
    { q: language === 'nl' ? 'Hoe snel kan ik beginnen?' : 'How fast can I start?', a: language === 'nl' ? 'Direct na betaling! Je krijgt binnen 30 seconden volledige toegang.' : 'Immediately after payment! Full access within 30 seconds.' },
    { q: language === 'nl' ? 'Is mijn betaling veilig?' : 'Is my payment secure?', a: language === 'nl' ? 'Absoluut. We gebruiken Stripe, de veiligste betaalprovider ter wereld.' : 'Absolutely. We use Stripe, the world\'s most secure payment provider.' }
  ];

  useEffect(() => {
    const loadListing = async () => {
      if (!listingId) { navigate('/dashboard'); return; }
      try {
        const data = await getListing(listingId);
        setListing(data);
      } catch (error) {
        console.error('Error loading listing:', error);
      } finally {
        setLoadingListing(false);
      }
    };
    loadListing();
  }, [listingId, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') pollPaymentStatus(sessionId);
    else if (status === 'cancelled') toast({ title: language === 'nl' ? 'Betaling geannuleerd' : 'Payment cancelled', variant: 'destructive' });
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) { setProcessingPayment(false); return; }
    setProcessingPayment(true);
    try {
      const result = await getPaymentStatus(sessionId);
      if (result.payment_status === 'paid') {
        setProcessingPayment(false);
        setPaymentSuccess(true);
        if (refreshUser) await refreshUser();
        toast({ title: language === 'nl' ? 'Welkom bij Premium!' : 'Welcome to Premium!' });
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) { navigate('/registreren'); return; }
    setProcessingPayment(true);
    try {
      const packageMap = { 'weekly': 'swipematch_weekly', 'monthly': 'swipematch_monthly', 'quarterly': 'swipematch_quarterly' };
      const result = await createCheckoutSession(packageMap[planId], `/upgrade-match/${listingId}`);
      if (result.url) window.location.href = result.url;
    } catch (error) {
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
      setProcessingPayment(false);
    }
  };

  if (loadingListing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#2E7D32] animate-spin mx-auto mb-4" />
          <p className="text-white/60">{language === 'nl' ? 'Laden...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137]" data-testid="upgrade-for-match-page">
      <Toaster />
      
      {/* Floating Urgency Bar */}
      <div className="bg-gradient-to-r from-[#C04A4A] to-[#E53935] text-white py-2.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span>{viewerCount} {language === 'nl' ? 'mensen bekijken deze woning nu' : 'people viewing this property now'}</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <button 
            onClick={() => {
              // Try to go back, if no history go to dashboard
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/dashboard');
              }
            }} 
            className="flex items-center gap-2 text-white/70 hover:text-white transition-all group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">{language === 'nl' ? 'Terug' : 'Back'}</span>
          </button>
          <SwipeMatchLogo size="small" variant="white" />
          <div className="w-20" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            
            {/* Left: Property Card */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-3xl blur-lg opacity-30"></div>
                
                <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl">
                  {/* Image */}
                  <div className="relative aspect-[16/10]">
                    {listing?.images?.[0] ? (
                      <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-white/30" />
                      </div>
                    )}
                    
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    
                    {/* Match badge */}
                    <div className="absolute top-4 left-4 bg-white px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                      <Heart className="w-4 h-4 text-[#C04A4A] fill-[#C04A4A]" />
                      <span className="text-sm font-bold text-[#2A2A2A]">{language === 'nl' ? 'Jouw match' : 'Your match'}</span>
                    </div>
                    
                    {/* Lock overlay */}
                    <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
                      <Lock className="w-4 h-4 text-white" />
                      <span className="text-xs font-medium text-white">{language === 'nl' ? 'Chat vergrendeld' : 'Chat locked'}</span>
                    </div>
                    
                    {/* Price */}
                    <div className="absolute bottom-4 left-4">
                      <div className="text-white">
                        <span className="text-4xl font-black">€{listing?.price || 0}</span>
                        <span className="text-white/80 text-lg ml-1">/{language === 'nl' ? 'mnd' : 'mo'}</span>
                      </div>
                    </div>
                    
                    {/* Size badge */}
                    {listing?.size && (
                      <div className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-white text-sm font-medium">{listing.size}m²</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-5">
                    <h2 className="text-xl font-bold text-[#2A2A2A] mb-1">{listing?.title || 'Woning'}</h2>
                    <div className="flex items-center gap-1.5 text-[#666]">
                      <MapPin className="w-4 h-4 text-[#2E7D32]" />
                      <span>{listing?.neighborhood ? `${listing.neighborhood}, ` : ''}{listing?.city || 'Nederland'}</span>
                    </div>
                    
                    {/* Locked message */}
                    <div className="mt-4 bg-[#F5F5F5] rounded-xl p-4 border-2 border-dashed border-[#E0E0E0]">
                      <div className="flex items-center gap-3 text-[#999]">
                        <Lock className="w-5 h-5" />
                        <span className="text-sm italic">{language === 'nl' ? 'Upgrade om een bericht te sturen...' : 'Upgrade to send a message...'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: CTA */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <Sparkles className="w-4 h-4" />
                Premium
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                {language === 'nl' ? 'Ontgrendel deze' : 'Unlock this'}<br/>
                <span className="text-[#2E7D32]">{language === 'nl' ? 'woning' : 'property'}</span>
              </h1>
              
              <p className="text-xl text-white/70 mb-8 max-w-md">
                {language === 'nl' 
                  ? 'Start direct een gesprek met de verhuurder en vergroot je kans op deze woning.'
                  : 'Start a conversation with the landlord and increase your chances.'}
              </p>
              
              {/* Quick stats */}
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start mb-8">
                <div className="text-center">
                  <div className="text-3xl font-black text-white">30.000+</div>
                  <div className="text-sm text-white/60">{language === 'nl' ? 'Succesvolle matches' : 'Successful matches'}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-[#2E7D32]">4.8</div>
                  <div className="text-sm text-white/60">{language === 'nl' ? 'Gemiddelde rating' : 'Average rating'}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-white">24u</div>
                  <div className="text-sm text-white/60">{language === 'nl' ? 'Gem. reactietijd' : 'Avg response time'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans-section" className="py-16 px-4 bg-white scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-[#2A2A2A] mb-3">
              {language === 'nl' ? 'Kies je abonnement' : 'Choose your plan'}
            </h2>
            <p className="text-[#666] text-lg">{language === 'nl' ? 'Geen verborgen kosten. Op elk moment opzegbaar.' : 'No hidden fees. Cancel anytime.'}</p>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                  selectedPlan === plan.id
                    ? 'bg-gradient-to-br ' + plan.color + ' text-white scale-105 shadow-2xl'
                    : 'bg-[#F8F9FA] hover:bg-[#F0F0F0] text-[#2A2A2A]'
                } ${plan.popular ? 'ring-4 ring-[#2E7D32] ring-offset-4' : ''}`}
              >
                {/* Popular badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2E7D32] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                    {language === 'nl' ? '⭐ MEEST GEKOZEN' : '⭐ MOST POPULAR'}
                  </div>
                )}
                
                {/* Save badge */}
                {plan.save && (
                  <div className={`absolute -top-2 -right-2 ${selectedPlan === plan.id ? 'bg-white text-[#C04A4A]' : 'bg-[#C04A4A] text-white'} text-xs font-bold px-3 py-1 rounded-full`}>
                    -{plan.save}
                  </div>
                )}

                <div className="text-center">
                  <p className={`text-sm font-medium mb-2 ${selectedPlan === plan.id ? 'text-white/80' : 'text-[#666]'}`}>{plan.duration}</p>
                  <div className="flex items-baseline justify-center gap-1 mb-1">
                    <span className={`text-lg ${selectedPlan === plan.id ? 'text-white/60' : 'text-[#999]'}`}>€</span>
                    <span className="text-5xl font-black">{plan.price}</span>
                  </div>
                  <p className={`text-sm ${selectedPlan === plan.id ? 'text-white/60' : 'text-[#999]'}`}>€{plan.weekPrice}/week</p>
                </div>

                <Button
                  onClick={(e) => { e.stopPropagation(); handleSubscribe(plan.id); }}
                  disabled={processingPayment}
                  className={`w-full mt-6 h-12 rounded-xl font-bold text-base transition-all ${
                    selectedPlan === plan.id
                      ? 'bg-white text-[#2A2A2A] hover:bg-white/90 shadow-lg'
                      : 'bg-[#2A2A2A] text-white hover:bg-[#1A1A1A]'
                  }`}
                >
                  {processingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>
                      {language === 'nl' ? 'Selecteer' : 'Select'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Features */}
          <div className="bg-[#F8F9FA] rounded-2xl p-8">
            <h3 className="text-xl font-bold text-[#2A2A2A] mb-6 text-center">{language === 'nl' ? 'Wat je krijgt' : 'What you get'}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {features.map((f, i) => (
                <div key={i} className={`flex flex-col items-center text-center p-4 rounded-xl ${f.highlight ? 'bg-[#2E7D32]/10' : ''}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${f.highlight ? 'bg-[#2E7D32] text-white' : 'bg-white shadow-md text-[#2E7D32]'}`}>
                    <f.icon className="w-6 h-6" />
                  </div>
                  <span className={`text-sm font-medium ${f.highlight ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'}`}>{f.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-10 text-[#999]">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">SSL Beveiligd</span>
            </div>
            <div className="flex items-center gap-2">
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-6 opacity-50" />
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="text-sm font-medium">{language === 'nl' ? 'Direct actief' : 'Instant access'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-[#FFB800] fill-[#FFB800]" />)}
            </div>
            <h2 className="text-3xl font-black text-[#2A2A2A] mb-2">
              {language === 'nl' ? 'Wat anderen zeggen' : 'What others say'}
            </h2>
            <p className="text-[#666]">{language === 'nl' ? 'Sluit je aan bij duizenden tevreden huurders' : 'Join thousands of happy renters'}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 ${t.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {t.initial}
                  </div>
                  <div>
                    <p className="font-bold text-[#2A2A2A]">{t.name}</p>
                    <p className="text-sm text-[#666]">{t.city}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#FFB800] fill-[#FFB800]" />)}
                </div>
                <p className="text-[#2A2A2A] leading-relaxed">"{t.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-black text-[#2A2A2A] text-center mb-10">
            {language === 'nl' ? 'Veelgestelde vragen' : 'FAQ'}
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-[#E5E5E5] rounded-xl overflow-hidden hover:border-[#2E7D32]/30 transition-colors">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#F8F9FA] transition-colors"
                >
                  <span className="font-semibold text-[#2A2A2A]">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#666] transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-5 text-[#666] leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-12 text-center">
            <Button
              onClick={() => {
                // Smooth scroll to plans section
                document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              disabled={processingPayment}
              className="h-14 px-10 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-full text-lg font-bold shadow-lg shadow-[#2E7D32]/30 hover:shadow-xl hover:shadow-[#2E7D32]/40 transition-all"
            >
              {processingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  {language === 'nl' ? 'Start nu met chatten' : 'Start chatting now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            <p className="text-sm text-[#999] mt-4">{language === 'nl' ? '30 dagen niet-goed-geld-terug garantie' : '30-day money-back guarantee'}</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UpgradeForMatchPage;
